import axios from 'axios';

import type { AxiosError, AxiosInstance } from 'axios';
import type { TApiErrorResponse } from '@/services/errors';

import { API_BASE_URL } from '@/constants/env';
import { logger } from '@/services/logger';

import type { TRetryableConfig } from './api.types';

const DEFAULT_TIMEOUT_MS = 15_000;
const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 400;
const RETRY_STATUSES = [408, 429, 500, 502, 503, 504];
const IDEMPOTENT_METHODS = ['get', 'head', 'options'];

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: { Accept: 'application/json' },
});

const canRetry = (config: TRetryableConfig, status?: number): boolean => {
  const attempt = config.retryCount ?? 0;

  if (attempt >= MAX_RETRIES) {
    return false;
  }

  const isAllowed = config.retry ?? IDEMPOTENT_METHODS.includes(config.method ?? '');

  if (!isAllowed) {
    return false;
  }

  return status === undefined || RETRY_STATUSES.includes(status);
};

const delay = (ms: number) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

apiClient.interceptors.response.use(undefined, async (error: AxiosError<TApiErrorResponse>) => {
  const config = error.config as TRetryableConfig | undefined;

  if (!config || !canRetry(config, error.response?.status)) {
    return Promise.reject(error);
  }

  const retryCount = (config.retryCount ?? 0) + 1;
  const waitMs = RETRY_BASE_DELAY_MS * 2 ** (retryCount - 1);

  logger.warn(`retrying ${config.method} ${config.url}`, { attempt: retryCount, waitMs });

  await delay(waitMs);

  const nextConfig: TRetryableConfig = { ...config, retryCount };

  return apiClient.request(nextConfig);
});

export {
  apiClient,
};
